import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { SESSION_STORAGE_REVIEW_KEY } from 'constants/appConstants';

import { omit } from 'helpers/lodashReplacement';
import { getFromSessionStorage, smoothScroll } from 'helpers/ClientUtils';
import MartyLink from 'components/common/MartyLink';
import AddReviewMediaUpload from 'components/reviews/AddReviewMediaUpload';
import AddReviewRating from 'components/reviews/AddReviewRating';
import AddReviewFitting from 'components/reviews/AddReviewFitting';
import AddReviewAbout from 'components/reviews/AddReviewAbout';
import AddReviewConfirmation from 'components/reviews/AddReviewConfirmation';
import ProductUtils from 'helpers/ProductUtils';
import css from 'styles/components/reviews/addReviewForm.scss';
import { ButtonSpinner } from 'components/Loader';

const MAX_SUMMARY_LENGTH = 10000;

export class AddReviewForm extends Component {
  state = {
    images: [],
    video: '',
    mounted: false
  };

  componentDidMount() {
    const { onUpdateSummary, onUpdateRating, onUpdateFit, onUpdateAbout, smoothScroll, authObj } = this.props;
    const persistedState = getFromSessionStorage(SESSION_STORAGE_REVIEW_KEY);
    this.setState({ mounted: true });

    if (authObj && authObj.success && Object.keys(persistedState).length > 0) {
      smoothScroll.scrollTo(this.reviewsSections);
    }

    const { summary, overallRating, comfortRating, lookRating, shoeSize, shoeWidth, shoeArch, reviewerName, reviewerLocation, otherShoes } = persistedState;

    if (summary) {
      onUpdateSummary(summary.slice(0, MAX_SUMMARY_LENGTH));
    }
    if (overallRating && overallRating !== '0') {
      onUpdateRating({ name: 'overallRating', value: overallRating });
    }
    if (comfortRating && comfortRating !== '0') {
      onUpdateRating({ name: 'comfortRating', value: comfortRating });
    }
    if (lookRating && lookRating !== '0') {
      onUpdateRating({ name: 'lookRating', value: lookRating });
    }
    if (shoeSize) {
      onUpdateFit({ name: 'shoeSize', value: shoeSize });
    }
    if (shoeWidth) {
      onUpdateFit({ name: 'shoeSize', value: shoeWidth });
    }
    if (shoeArch) {
      onUpdateFit({ name: 'shoeSize', value: shoeArch });
    }
    if (reviewerName) {
      onUpdateAbout({ name: 'reviewerName', value: reviewerName });
    }
    if (reviewerLocation) {
      onUpdateAbout({ name: 'reviewerLocation', value: reviewerLocation });
    }
    if (otherShoes) {
      onUpdateAbout({ name: 'otherShoes', value: otherShoes });
    }
  }

  onAboutChange = ({ target }) => {
    const { onUpdateAbout } = this.props;
    const { value } = target;
    const name = target.getAttribute('data-about');
    onUpdateAbout({ name, value });
  };

  onFitChange = ({ target }) => {
    const { onUpdateFit } = this.props;
    const { value } = target;
    const name = target.getAttribute('data-fitting');
    onUpdateFit({ name, value });
  };

  onMediaAttachment = mediaObj => {
    this.setState(mediaObj);
  };

  onRatingChange = ({ target }) => {
    const { onUpdateRating } = this.props;
    const { value } = target;
    const name = target.getAttribute('data-rating');
    onUpdateRating({ name, value });
  };

  onReviewSummaryChange = ({ target }) => {
    const { onUpdateSummary } = this.props;
    const { value, maxLength } = target;
    if (value.length <= maxLength) {
      onUpdateSummary(value);
    }
  };

  onSubmitReview = () => {
    const { images, video } = this.state;
    const { params: { productId, colorId, p: isPremierReviewer }, product: { defaultProductType }, returnTo, addReview, onSubmitProductReview } = this.props;
    let customerReview = omit(addReview, ['errorMap', 'isLoading', 'isSubmitted']);
    if (!ProductUtils.isShoeType(defaultProductType)) {
      customerReview = omit(customerReview, ['shoeSize', 'shoeWidth', 'shoeArch', 'otherShoes']);
    }
    const reviewMedia = { images, video };
    const reviewData = { productId, ...customerReview, premier: !!isPremierReviewer };
    if (colorId && !isNaN(parseInt(colorId))) {
      reviewData.colorId = colorId;
    }
    onSubmitProductReview({ reviewData, reviewMedia, returnTo });
  };

  onSubmitValidate = () => {
    const { addReview, onAddFieldValidationErrors, smoothScroll } = this.props;
    const { summary, overallRating, comfortRating, lookRating } = addReview;

    const errorMap = {};

    if (overallRating === '0') {
      errorMap.overallRating = true;
    }
    if (comfortRating === '0') {
      errorMap.comfortRating = true;
    }
    if (lookRating === '0') {
      errorMap.lookRating = true;
    }
    if (!summary) {
      errorMap.summary = true;
    }

    if (Object.keys(errorMap).length > 0) {
      onAddFieldValidationErrors(errorMap);
      smoothScroll.scrollTo(this.reviewsSections);
    } else {
      this.onSubmitReview();
    }
  };

  render() {
    const { images, video, mounted } = this.state;
    const { testId } = this.context;
    const { authObj, boolMediaUpload, addReview, product, params: { productId } } = this.props;
    const { defaultProductType, isReviewableWithMedia } = product;
    const {
      errorMap, isSubmitted, isLoading, summary,
      overallRating, comfortRating, lookRating, shoeSize, shoeWidth, shoeArch,
      reviewerName, reviewerLocation, otherShoes, submitReviewErrorMessage
    } = addReview;
    const ratings = { overallRating, comfortRating, lookRating };
    const isShoeType = ProductUtils.isShoeType(defaultProductType);
    const showMediaUpload = boolMediaUpload ? isReviewableWithMedia && authObj.success && mounted : isReviewableWithMedia && mounted;

    if (isSubmitted && !submitReviewErrorMessage) {
      return <AddReviewConfirmation productId={productId}/>;
    }

    return (
      <div>
        <ol className={css.stepList} ref={el => this.reviewsSections = el}>
          <li> {/* 1. Product rating and fit */}
            <div className={cn(css.fieldSection, css.ratingSection)}>
              <h2 className={css.fieldLabel} data-test-id={testId('reviewQ1')}>How did you feel about this item?</h2>
              <AddReviewRating ratings={ratings} errors={errorMap} onRatingChange={this.onRatingChange}/>
              {isShoeType && (
                <>
                  <h3 className={cn(css.fieldLabel, css.shoeDimensionsLabel)}>What about the fit?</h3>
                  <AddReviewFitting
                    shoeSize={shoeSize}
                    shoeWidth={shoeWidth}
                    shoeArch={shoeArch}
                    onFitChange={this.onFitChange}/>
                </>
              )}
            </div>
          </li>
          <li> {/* 2. Review summary */}
            <div className={css.fieldSection}>
              <h2 className={css.fieldLabel}>Let us know what you think</h2>
              <label className={css.summaryMelodyInputLabel} htmlFor="summary">Your Review</label>
              <textarea
                className={cn(css.summaryField, { [css.error]: errorMap.summary })}
                id="summary"
                data-test-id={testId('summary')}
                maxLength={MAX_SUMMARY_LENGTH}
                placeholder="How was the fit and size? What does the material feel like? How awesome (or not) do you feel wearing them?"
                onChange={this.onReviewSummaryChange}
                value={summary}/>
            </div>
          </li>
          {showMediaUpload && (
            <li> {/* 3. Photo and video upload */}
              <div className={cn(css.fieldSection, css.uploadFieldSection)}>
                <span className={cn(css.label, css.fieldLabel)}>Upload photos and videos <span className={css.fieldLabel}>(optional)</span></span>
                <div className={cn(css.fieldLabel, css.removeFieldLabelMargin)}>You can drag &amp; drop or click below to attach up to four images and/or one video</div>
                <AddReviewMediaUpload images={images} video={video} onMediaAttachment={this.onMediaAttachment}/>
              </div>
            </li>
          )}
          <li> {/* 4. Tell us about yourself */}
            <div className={css.fieldSection}>
              <h2 className={cn(css.fieldLabel, css.aboutSectionLabel)}>Tell us a little about yourself</h2>
              <AddReviewAbout
                reviewerName={reviewerName}
                reviewerLocation={reviewerLocation}
                otherShoes={otherShoes}
                errors={errorMap}
                onAboutChange={this.onAboutChange} />
              <div className={css.bottomNote}>
                Note: By submitting a review you represent and warrant that you are at least 18 years old. Please visit our <MartyLink to="/c/customer-reviews">Review Guidelines</MartyLink> for full guidelines on writing reviews. All content (including text, images and videos) becomes our sole and exclusive property pursuant to our <MartyLink to="/c/terms-of-use">Terms of Use</MartyLink>. Your reviews will be processed within five days.
              </div>
              <div className={css.fieldSection}>
                <button
                  type="button"
                  className={css.submitButton}
                  onClick={this.onSubmitValidate}
                  disabled={isLoading}
                  data-test-id={testId('submitReviewButton')}>
                  {isLoading ? <ButtonSpinner size={15}/> : 'Submit'}
                </button>
              </div>
            </div>
          </li>
        </ol>
      </div>
    );
  }
}

AddReviewForm.propTypes = {
  addReview: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  returnTo: PropTypes.string.isRequired,
  onUpdateSummary: PropTypes.func.isRequired,
  onUpdateRating: PropTypes.func.isRequired,
  onUpdateFit: PropTypes.func.isRequired,
  onUpdateAbout: PropTypes.func.isRequired,
  onSubmitProductReview: PropTypes.func.isRequired,
  onAddFieldValidationErrors: PropTypes.func.isRequired,
  authObj: PropTypes.object,
  boolMediaUpload: PropTypes.bool
};

AddReviewForm.defaultProps = {
  smoothScroll // allow mocking of smoothScroll for unit tests
};

AddReviewForm.contextTypes = {
  testId:PropTypes.func
};

export default AddReviewForm;
